import React, { useEffect, useState } from 'react';
import { Building2, GraduationCap, Briefcase, MapPin, FileText, AlertCircle, Loader2, Download } from 'lucide-react';

interface ShareData {
  candidate: {
    id: string;
    name: string;
    education: string;
    school: string;
    major: string;
    work_years: number;
    age: number;
    latest_company: string;
    latest_title: string;
    work_experiences: string;
    project_experiences: string;
    resume_file: string;
    resume_text: string;
    expected_salary: string;
    expected_city: string;
    position_name: string;
    department: string;
    location: string;
  };
  company: string;
  expires_at: string;
}

export default function CandidateShareView() {
  const token = window.location.pathname.split('/').filter(Boolean)[1] || '';
  const [data, setData] = useState<ShareData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setError('链接无效'); setLoading(false); return; }
    fetch(`/api/share/${token}`)
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message); }); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message || '加载失败'); setLoading(false); });
  }, [token]);

  useEffect(() => {
    if (data) {
      document.title = `${data.candidate.name}${data.candidate.position_name ? ` · ${data.candidate.position_name}` : ''} - ${data.company}`;
    }
  }, [data]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-7 h-7 text-gray-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium mb-1">{error}</p>
        <p className="text-sm text-gray-400">链接可能已过期或不存在</p>
      </div>
    </div>
  );

  if (!data) return null;
  const { candidate: c, company } = data;

  const resumeUrl = c.resume_file || null;
  const isPdf = resumeUrl && resumeUrl.toLowerCase().includes('.pdf');
  const expireDate = new Date(data.expires_at).toLocaleDateString('zh-CN');

  const infoChips = [
    c.education && `${c.education}${c.school ? ' · ' + c.school : ''}${c.major ? ' · ' + c.major : ''}`,
    c.work_years > 0 && `${c.work_years}年经验`,
    c.latest_title && c.latest_title,
    c.latest_company && c.latest_company,
    c.expected_city && c.expected_city,
  ].filter(Boolean) as string[];

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
          {c.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{c.name}</span>
            {c.position_name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                <Briefcase size={10} />应聘：{c.position_name}{c.department ? ` · ${c.department}` : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {infoChips.map((chip, i) => (
              <span key={i} className="text-xs text-gray-500">{chip}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {resumeUrl && (
            <a
              href={resumeUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Download size={13} />下载简历
            </a>
          )}
          <span className="text-xs text-gray-400 hidden sm:inline">有效至 {expireDate}</span>
        </div>
      </div>

      {/* Resume body */}
      <div className="flex-1 overflow-hidden">
        {isPdf ? (
          <iframe
            src={`${resumeUrl}#toolbar=1&navpanes=0`}
            className="w-full h-full border-0"
            title={`${c.name} 简历`}
          />
        ) : resumeUrl ? (
          <div className="h-full flex flex-col items-center justify-center gap-4 bg-gray-50">
            <FileText className="w-16 h-16 text-gray-300" />
            <p className="text-gray-500 text-sm">该简历格式需下载后查看</p>
            <a
              href={resumeUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={15} />下载完整简历
            </a>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-2 bg-gray-50">
            <FileText className="w-12 h-12 text-gray-300" />
            <p className="text-gray-400 text-sm">暂无简历文件</p>
          </div>
        )}
      </div>
    </div>
  );
}
